/* jshint esversion: 6 */

import React from 'react';
import PropTypes from 'prop-types';
import LocalTime from "local-time";
import ScrollToBottom from 'react-scroll-to-bottom';

import { AvoCable } from '../../../lib/avo-cable';
import ReliableRequest from '../../../lib/ReliableRequest.js';
import WebsiteWidgetMessage from './WebsiteWidgetMessage.js';
import { WebsiteWidgetChatHeader } from './WebsiteWidgetChatHeader';
import { ChatStatus } from './ChatStatus';
import SendButton from '../../../images/send.svg';
import AvochatoImage from '../../../images/favicon-256.ico';

const HEARTBEAT_INTERVAL = 10000;
export default class WebsiteWidgetChat extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      startTime: new Date(),
      status: ChatStatus.UNASSIGNED,
      username: null,
      typing: false,
    };
    this.typingChecker = null;
    this.lastHeartbeat = new Date();
    this.nextHeartbeat = null;
    LocalTime.start();
    this.props.headerChange(this.props.agentWaitingText)
  }

  componentDidMount() {
    this.subscription = AvoCable.subscriptions.create({
      channel: "V3::LiveChatChannel",
      live_session: this.props.sessionId,
    }, {
      connected: () => this.onConnect(),
      received: (e) => {
        this.handleEvent(e);
        this.props.onEvent(e);
      },
    });
  }

  onConnect() {
    this.subscription.send({
      event: 'join',
      live_session: this.props.sessionId,
    });

    this.checkTyping();
    this.startHeartbeat();
    this.fetchMissedEvents();
  }

  componentWillUnmount() {
    if (this.subscription) this.subscription.unsubscribe();
    if (this.typingChecker) clearInterval(this.typingChecker);
    if (this.nextHeartbeat) clearTimeout(this.nextHeartbeat);
  }

  render() {
    return (
      <div
        id="thank-you">
        {this.renderLiveChat()}
      </div>
    );
  }

  renderLiveChat() {
    const { className, sessionId } = this.props;
    const { startTime, status, username } = this.state;
    return (
      <div id="live-chat" role="log" aria-live="polite" className={className}>
        {status === ChatStatus.UNASSIGNED &&
          <WebsiteWidgetChatHeader
            sessionId={sessionId}
            startTime={startTime}
            chatStatus={status}
            agent={username}
          />
        }
        {this.renderMessages()}
        {this.renderPoweredBy()}
        {this.renderChatbox()}
      </div>
    );
  }
  renderPoweredBy() {
    if (this.props.whiteLabel) {
      return (
        <div id="powered-by" className="no-select"></div>
      )
    }
    return (
      <div id="powered-by" className="no-select">
        <img
          id="logo-img"
          alt="Avochato - Texting for Business"
          src={AvochatoImage} />
        Powered by Avochato
      </div>
    )
  }
  renderMessages() {
    return (
      <ScrollToBottom className="events">
        {this.props.messages.map((i) => this.renderMessage(i))}
        {this.renderTyping()}

      </ScrollToBottom>
    );
  }

  renderMessage(m) {
    return (
      <WebsiteWidgetMessage
        key={m.type + '-' + m.id}
        type={m.type}
        id={m.id}
        username={this.state.username}
        direction={m.direction}
        messageText={m.messageText}
        status={m.status}
        sentAt={m.sentAt}
        primaryColor={this.props.primaryColor}
        contents={m.contents}
        />
    );
  }

  renderTyping() {
    if (!this.state.typing) return null;
    return (
      <div class="message-box out" data-status="delivered" data-direction="out">
        <div className="avatar typing">
          {(this.state.username||'A').charAt(0).toUpperCase()}
        </div>
        <div class="outbound-message typing">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    );
  }

  renderChatbox() {
    const { status } = this.state;
    return (
      <div className={"chatbox-form" + (status === ChatStatus.OPEN ? '' : ' hide')}>
        <textarea
          id="chat-message"
          className="chat-textarea"
          name="chat_message"
          placeholder={this.props.chatPlaceholder}
          onKeyDown={(e) => this.onKeyDown(e)}
          ref={
            (el) => {
              this.inputRef = el;
              this.props.chatMessageInputRef(el);
            }
          } />
        <button type="submit" className="submit-button" name="commit" value="Send" ref={(el) => this.submitRef = el}>
          <img src={SendButton} alt="Send" />
        </button>
      </div>
    );
  }

  fetchMissedEvents() {
    ReliableRequest.dispatch({
      url: window.location.pathname + '/events.json',
      method: 'GET',
      data: {live_session: this.props.sessionId},
      onSuccess: (data) => {
        this.setState({username: data.ticket.username}, () => this.handleStatusChange(data.ticket.status));
        data.events.forEach((e) => this.props.onEvent(e));
      },
      onError: (error) => {
        // if we fail to find the ticket after multiple attempts, close it out
        if (error.status === 404) {
          this.handleStatusChange('Closed');
        }
      },
      whitelist: [400],
    });
  }

  handleEvent(e) {
    switch (e.event) {
      case 'join':
      case 'ping':
        this.fetchMissedEvents();
        break;
      case 'StatusChange':
        this.handleStatusChange(e.status);
        break;
      case 'OwnerChange':
        this.handleOwnerChange(e.user);
        break;
      case 'typing_start':
        this.setState({typing: true});
        break;
      case 'typing_stop':
        this.setState({typing: false});
        break;
    }
  }

  handleStatusChange(status) {
    if (['Pending', 'Closed'].indexOf(status) > -1) {
      this.closeConversation();
    } else if (this.state.username) {
      this.openConversation(this.state.username);
    }
  }

  handleOwnerChange(user) {
    if (!user) return;
    if (this.state.username) return; // already has an owner

    this.openConversation(user.username);
  }

  closeConversation() {
    this.setState({
      status: ChatStatus.CLOSED,
      username: null,
    });
    this.props.headerChange(this.props.chatEndedText)
    // there were no messages and you asked to close the widget so resetting
    if (this.props.messages.length === 0) this.props.onMissing();
    if (typeof(this.props.onClose) === 'function') return this.props.onClose();
  }

  openConversation(username) {
    this.setState({
      status: ChatStatus.OPEN,
      username: username,
    });
    this.props.headerChange('Now chatting with ' + this.state.username)
    if (typeof(this.props.onOpen) === 'function') return this.props.onOpen();
  }

  onKeyDown(e) {
    if (!e.shiftKey && e.keyCode === 13) {
      e.preventDefault();
      this.submitRef.click();
      return;
    }

    const input = e.target;
    if (!input) return;

    input.setAttribute('data-newlines', (input.value.match(/\n/g)||[]).length > 0);
  }

  checkTyping() {
    if (this.typingChecker) return;

    this.typingChecker = setInterval(() => {
      if (this.inputRef && this.inputRef.value.length > 0) {
        this.typing = true;
        this.subscription.send({
          event: 'typing_start',
          live_session: this.props.sessionId,
        });
        return;
      }

      if (!this.typing) return;
      this.typing = false;
      this.subscription.send({
        event: 'typing_stop',
        live_session: this.props.sessionId,
      });
    }, 1000);
  }

  startHeartbeat() {
    if (this.nextHeartbeat) return;

    this.triggerHeartbeat();
  }

  triggerHeartbeat() {
    if (new Date() - this.lastHeartbeat > HEARTBEAT_INTERVAL) {
      console.warn('heartbeat took too long, going to fetch missing events in case');
      this.fetchMissedEvents();
    }

    this.subscription.send({
      event: 'heartbeat',
      live_session: this.props.sessionId,
    });
    this.lastHeartbeat = new Date();
    this.nextHeartbeat = setTimeout(() => {
      this.triggerHeartbeat();
    }, HEARTBEAT_INTERVAL - 500);
  }
}

WebsiteWidgetChat.propTypes = {
  className: PropTypes.string,
  chatMessageInputRef: PropTypes.func,
  sessionId: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.shape({
    contents: PropTypes.array,
    type: PropTypes.string,
    id: PropTypes.string,
    direction: PropTypes.string,
    messageText: PropTypes.string,
    status: PropTypes.string,
    sentAt: PropTypes.number,
  })),
  onOpen: PropTypes.func,
  onEvent: PropTypes.func,
  onMissing: PropTypes.func,
  onClose: PropTypes.func,
  status: PropTypes.func,
};
